import { Injectable } from "@angular/core";
import { Subscription } from "src/services/subscription/subscription";
import { ApiService } from "../http/api.service";
import { apiPath } from "../../shared/api-path.const";
import {
  SubscriptionResponse,
  SubscriptionsForUserResponse,
  NeedsBaseSelectionResponse,
  UpdateSubscriptionRequest,
} from "../../shared/backend-api.types";
import { SubscriptionTypeService } from "../subscription-type/subscription-type.service";

function toSubscription(response: SubscriptionResponse): Subscription {
  return {
    id: response.subscriptionId,
    baseLocation: (response.baseLocation ?? undefined) as Subscription["baseLocation"],
    baseSelectedByUser: response.baseSelectedByUser,
    interval: response.interval as Subscription["interval"],
    price: response.price,
    status: response.status,
    subscriptionDate: new Date(response.subscriptionDate),
    subscriptionTypeId: response.subscriptionTypeId as Subscription["subscriptionTypeId"],
    userId: response.userId,
    hasPromoCode: false,
    subscriptionType: response.subscriptionTypeName ?? "",
  };
}

@Injectable({
  providedIn: "root",
})
export class SubscriptionApiService {
  constructor(
    private readonly apiService: ApiService,
    private readonly subscriptionTypeService: SubscriptionTypeService,
  ) {}

  public async getById(id: string): Promise<Subscription> {
    const response = await this.apiService.sendGetRequest<SubscriptionResponse>(
      `${apiPath.subscription}/${id}`,
      undefined,
    );
    return toSubscription(response);
  }

  public async getByUserId(userId: string): Promise<Subscription[]> {
    const response = await this.apiService.sendGetRequest<SubscriptionsForUserResponse>(
      `${apiPath.subscription}/user/${userId}`,
      undefined,
    );
    return response.subscriptions.map(toSubscription);
  }

  public async getFirstByUserId(userId: string): Promise<Subscription | undefined> {
    const subscriptions = await this.getByUserId(userId);
    return subscriptions[0];
  }

  public async update(id: string, updates: Partial<Subscription>): Promise<void> {
    const body: UpdateSubscriptionRequest = {};
    if (updates.baseLocation) {
      body.baseLocation = updates.baseLocation as unknown as UpdateSubscriptionRequest["baseLocation"];
    }
    if (updates.status) {
      body.status = updates.status as UpdateSubscriptionRequest["status"];
    }
    await this.apiService.sendPatchRequest<UpdateSubscriptionRequest, void>(`${apiPath.subscription}/${id}`, body);
  }

  public async userHasAnySubscriptionWithoutBase(userId: string): Promise<boolean> {
    const response = await this.apiService.sendGetRequest<NeedsBaseSelectionResponse>(
      `${apiPath.subscription}/user/${userId}/needs-base`,
      undefined,
    );
    return response.needsBase;
  }

  public async getAll(): Promise<Subscription[]> {
    const response = await this.apiService.sendGetRequest<SubscriptionResponse[]>(apiPath.subscription, undefined);
    return response.map(toSubscription);
  }

  public async generateNewBasicSubscriptionFromPriceId(userId: string, priceId: string): Promise<Subscription> {
    const subscriptionType = await this.subscriptionTypeService.getSubscriptionTypeFromPriceId(priceId);
    const plan = subscriptionType
      ? this.subscriptionTypeService.getPlanWithPriceIdFromSubscriptionType(subscriptionType, priceId)
      : undefined;

    if (!subscriptionType || !plan) {
      throw new Error(`No subscription plan matches the price ${priceId}.`);
    }

    return {
      baseLocation: undefined,
      price: plan.price,
      status: "active",
      subscriptionDate: new Date(),
      interval: plan.interval,
      hasPromoCode: false,
      promoCode: "",
      userId: userId,
      subscriptionType: subscriptionType.name,
      subscriptionTypeId: subscriptionType.id,
    };
  }
}
